import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PurchaseCreditNoteRegisterForm from '../components/PurchaseCreditNoteRegisterForm';
import { useGetPurchaseCreditNoteRegisterHandler } from '../dataHandlers/getPurchaseCreditNoteRegisterHandler';
import { useUpdatePurchaseCreditNoteRegisterHandler } from '../dataHandlers/updatePurchaseCreditNoteRegisterHandler';
import DocumentSkeletonForm from '../../../components/DocumentSkeletonForm';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_PURCHASE_REGISTER } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditPurchaseCreditNoteRegisterPage() {
  const { purchaseInvoiceRegister } = useTranslatedMessages();
  useDocumentTitle(
    purchaseInvoiceRegister?.pages?.edit_title_credit_note ?? 'Edit Purchase Credit Note Register Entry',
  );
  const navigate = useNavigate();
  const { document_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { register, isLoading } = useGetPurchaseCreditNoteRegisterHandler(document_id);

  useEffect(() => {
    if (loading) return;
    if (!hasEInvoicePermission(UPDATE_PURCHASE_REGISTER)) {
      showActionDeniedSnackbar();
      navigate('/purchase-invoice-register?tab=credit-notes', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isLoading && register && register.document_mode !== 'NON_E_INVOICE') {
      const documentId = register.uuid || register.id;
      if (register.document_mode === 'E_INVOICE') {
        navigate(`/purchases/documents/credit-notes/${documentId}`, { replace: true });
      } else {
        navigate('/purchase-invoice-register?tab=credit-notes', { replace: true });
      }
    }
  }, [isLoading, register, navigate]);

  const onRegisterUpdated = () => {
    navigate('/purchase-invoice-register?tab=credit-notes', { replace: true });
  };

  const { onSubmit, error, isPending } = useUpdatePurchaseCreditNoteRegisterHandler({ onSuccess: onRegisterUpdated });

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onSaveForm = (data) => {
    setIsSubmitting(true);
    onSubmit({ id: document_id, payload: data });
  };

  const onCancel = () => {
    navigate('/purchase-invoice-register?tab=credit-notes');
  };

  if (isLoading || !register) {
    return <DocumentSkeletonForm />;
  }

  if (register.document_mode !== 'NON_E_INVOICE') {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <PurchaseCreditNoteRegisterForm
        initialData={register}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
